<template>
  <div class="grouped-session">
    <template v-if="activeGroup">
      <div class="p-content bg-white mb-5">
        <transition name="fade-out-left" mode="out-in">
          <workout-group
            :key="activeGroup.id"
            :group="activeGroup"
            :progress="groupTimer ? groupTimer.percentComplete / 100 : 0"
            active
          />
        </transition>
      </div>
    </template>

    <cta-row
      white-bg
      no-spacing
      :overlay-modal="!($route.hash || '').startsWith('#')"
    >
      <workout-timer
        v-if="!(hideTimer && workoutTimer.status !== TIMER_STATUS.COUNTING_IN)"
        class="mt-1"
        :workoutTimer="workoutTimer"
        :currentTimer="groupTimer"
      >
        <beep-test-stats v-if="isBeepTest" :activeGroup="activeGroup" />
      </workout-timer>

      <v-button
        block
        ghost
        class="my-1"
        @click="forceAdvance"
        secondary
        :disabled="!isWorkoutStarted"
        :is-loading="isLoading"
      >
        {{ completeText }}
      </v-button>
    </cta-row>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex"

import { WORKOUT_TYPES } from "@/store/workouts"

import EventBus, { EVENTS } from "@/lib/event-bus"
import Timer, {
  TIMER_STATUS,
  TIMER_EVENTS,
  minutesToSeconds
} from "@/lib/timer"
import { toSprite } from "@/lib/audio-utils"
import enumValidator from "@/lib/validators/enum-validator"

import CtaRow from "@/components/CtaRow"
import WorkoutGroup from "@/components/workout/Group"
import WorkoutTimer from "@/components/workout/LegacyTimer"

const ACTIVITY_TYPES = {
  GROUP_STARTED: "workout_group_started",
  GROUP_COMPLETED: "workout_group_completed"
}

export default {
  components: {
    CtaRow,
    WorkoutGroup,
    WorkoutTimer,
    BeepTestStats: () => import("@/components/workout/custom/BeepTestStats")
  },

  props: {
    groups: { type: Array, required: true },
    session: { type: Object, required: true },
    workoutId: { type: Number, required: true },
    workoutTimer: { type: Object, required: true },
    workoutType: {
      type: String,
      default: null,
      validator: enumValidator([null, WORKOUT_TYPES.BEEP_TEST])
    },
    hideTimer: { type: Boolean, default: false }
  },

  data() {
    return {
      isWorkoutStarted: false,
      activeGroupIndex: 0,
      groupTimer: null,
      isLoading: false,
      TIMER_STATUS
    }
  },

  mounted() {
    EventBus.$once(EVENTS.WORKOUT_STARTED, () => {
      this.startGroupTimer()
      this.isWorkoutStarted = true
    })
  },

  methods: {
    ...mapActions("workouts", ["recordSessionActivity"]),

    startGroupTimer() {
      if (this.activeGroupHasTimer) {
        if (this.activeGroup.is_rest) {
          const seconds =
            this.activeGroup.rest_interval === "sec"
              ? this.activeGroup.rest_period
              : minutesToSeconds(this.activeGroup.rest_period)

          this.groupTimer = new Timer({
            seconds,
            countDirection: "down"
          })
        } else {
          const seconds =
            this.activeGroup.timer_interval === "sec"
              ? this.activeGroup.timer_period
              : minutesToSeconds(this.activeGroup.timer_period)

          this.groupTimer = new Timer({
            seconds,
            countDirection: "down"
          })
        }

        this.groupTimer.on(TIMER_EVENTS.FINISHED, () => this.startNextGroup())
        this.groupTimer.start(this.activeGroupAudio)
      }
    },

    forceAdvance() {
      if (this.isBeepTest) {
        this.completeSession()
      } else {
        if (this.hasActiveTimer) this.groupTimer.pause()

        if (this.activeGroup) {
          this.startNextGroup()
        } else {
          this.startGroupTimer()
        }
      }
    },

    startNextGroup() {
      if (this.isLastGroup) {
        this.completeSession()
      } else {
        if (this.hasActiveTimer) {
          this.groupTimer.reset()
          this.groupTimer = null
        }
        this.activeGroupIndex += 1
        this.startGroupTimer()
      }
    },

    recordActivity(group, activityType) {
      return this.recordSessionActivity({
        workoutId: this.workoutId,
        sessionId: this.session.id,
        groupId: group.id,
        activityType
      })
    },

    completeSession() {
      if (this.hasActiveTimer) this.groupTimer.pause()

      this.recordActivity(
        this.activeGroup,
        ACTIVITY_TYPES.GROUP_COMPLETED
      ).then(() => {
        this.$emit("complete")
      })
    },

    cleanUpSession() {
      if (this.groupTimer) {
        this.groupTimer.pause()
        this.groupTimer.reset()
        this.groupTimer = null
      }

      this.activeGroupIndex = 0
      this.isWorkoutStarted = false
    }
  },

  beforeDestroy() {
    this.cleanUpSession()
  },

  computed: {
    ...mapGetters("auth", ["isAuthenticated"]),

    hasActiveTimer() {
      return this.activeGroupHasTimer && this.groupTimer
    },

    isLastGroup() {
      if (!this.activeGroup) return false

      return !this.groups[this.activeGroupIndex + 1]
    },

    completeText() {
      if (this.isBeepTest) {
        return "Complete Beep Test"
      } else if (this.activeGroup) {
        return this.activeGroup.is_rest
          ? "Skip Rest"
          : `Complete ${this.activeGroup.title}`
      } else {
        return "Complete Workout"
      }
    },

    activeGroup() {
      return typeof this.activeGroupIndex === "number"
        ? this.groups[this.activeGroupIndex]
        : null
    },

    activeGroupAudio() {
      return this.activeGroup.audio_start || this.activeGroup.audio_end
        ? {
            start: toSprite(this.activeGroup.audio_start),
            end: toSprite(this.activeGroup.audio_end)
          }
        : null
    },

    activeGroupHasTimer() {
      if (!this.activeGroup) return false

      return this.activeGroup.is_rest ? true : this.activeGroup.has_timer
    },

    isBeepTest() {
      return this.workoutType === WORKOUT_TYPES.BEEP_TEST
    }
  },

  watch: {
    activeGroup: {
      immediate: true,
      handler(current, last) {
        if (current && last) {
          this.recordActivity(last, ACTIVITY_TYPES.GROUP_COMPLETED).then(() =>
            this.recordActivity(current, ACTIVITY_TYPES.GROUP_STARTED)
          )
        } else if (current) {
          this.recordActivity(current, ACTIVITY_TYPES.GROUP_STARTED)
        }
      }
    }
  }
}
</script>
